import React, { useState } from "react";
import { Building2, HardHat, Ruler, Home, Truck, ClipboardCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { PageHero, Section, SectionHeading, SecondaryButton } from "@/components/ui-helpers";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AUDIENCE = [
  { icon: HardHat, t: "Contractors & Developers" }, { icon: Ruler, t: "Architects & Interior Designers" },
  { icon: Home, t: "Real Estate Agents" }, { icon: Building2, t: "Property Managers" },
  { icon: Truck, t: "Moving Companies" }, { icon: ClipboardCheck, t: "Professional Organizers" },
];
const BENEFITS = ["Fast photo/video assessments", "Written scopes", "Reliable scheduling", "Post-construction specialists", "Before-and-after documentation", "Priority scheduling for partners", "Client-ready handovers", "Ongoing partnerships", "Service throughout Israel"];

export default function ForProfessionals() {
  const [form, setForm] = useState({ company: "", contact_name: "", phone: "", email: "", business_type: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.phone) { toast.error("Please fill in company, name and phone."); return; }
    setBusy(true);
    try { await api.post("/partner-inquiries", form); setSent(true); toast.success("Thanks — we'll be in touch shortly."); }
    catch { toast.error("Something went wrong. Please try again."); } finally { setBusy(false); }
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      <SEO title="For Professionals" description="A reliable final-clean and detailing partner for contractors, architects, agents and property managers across Israel." />
      <PageHero kicker="For Professionals" title="A reliable final-clean partner for every property handover" subtitle="Detailing-grade results, written scopes and dependable scheduling for the professionals who deliver properties." image="https://images.unsplash.com/photo-1600585152220-90363fe7e115?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600">
        <SecondaryButton to="/partners/login">Partner Portal Login / Register</SecondaryButton>
      </PageHero>
      <Section>
        <SectionHeading kicker="Who we partner with" title="Built for property professionals" />
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {AUDIENCE.map((a) => (
            <div key={a.t} className="ascendia-card card-accent-top p-6 flex items-center gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/10 border border-[color:var(--border-subtle)]"><a.icon className="h-5 w-5 text-gold-soft" strokeWidth={1.5} /></div>
              <span className="text-white font-medium">{a.t}</span>
            </div>
          ))}
        </div>
      </Section>
      <Section className="bg-[#0e0e0f] border-y border-[color:var(--border-subtle)]">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <SectionHeading kicker="Partner benefits" title="What you get with Ascendia" />
            <ul className="mt-8 space-y-3">{BENEFITS.map((b) => <li key={b} className="flex items-center gap-3 text-secondary"><CheckCircle2 className="h-5 w-5 text-gold" /> {b}</li>)}</ul>
          </div>
          <div className="ascendia-card p-7" data-testid="partner-inquiry-form">
            {sent ? (
              <div className="text-center py-10">
                <CheckCircle2 className="h-12 w-12 text-gold mx-auto" />
                <h3 className="mt-4 font-display text-xl font-semibold text-white">Inquiry received</h3>
                <p className="mt-2 text-secondary">Thank you. Our team will reach out to discuss a partnership.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <h3 className="font-display text-xl font-semibold text-white">Partnership inquiry</h3>
                {[["company", "Company name*"], ["contact_name", "Contact name*"], ["phone", "Phone*"], ["email", "Email"], ["business_type", "Business type (e.g. contractor)"]].map(([k, label]) => (
                  <input key={k} value={form[k]} onChange={upd(k)} placeholder={label} data-testid={`partner-${k}-input`} className="w-full rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]" />
                ))}
                <textarea value={form.message} onChange={upd("message")} rows={3} placeholder="Tell us about your typical projects…" className="w-full rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]" />
                <button type="submit" disabled={busy} data-testid="partner-submit-button" className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-6 py-3 text-sm font-semibold text-black hover:bg-gold-soft transition-colors disabled:opacity-50">
                  {busy ? "Sending…" : "Submit Inquiry"} <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </Section>
    </>
  );
}
